// Vosk WASM backend exposing the surface LocalPlacementAsrHandler reads:
// sampleRate, windowMs, _engine.windowSamples, decode.streaming/consensus,
// transcribe(pcm, {wordList|graph}), and dispose. The handler owns the rolling
// window and consensus, so transcribe decodes one fixed window per call against a
// recognizer cached by grammar (retrieveFinalResult resets state between windows).
//
// transcribe is serialized (single-flight, like the NeMo engine's _tail queue):
// exactly one window decodes at a time across every grammar/mode/warmup call, so a
// recognizer's mutable decode state can never be shared by two overlapping calls.

import { logSessionInfo } from '../../logger';
import { recordLocalAsrFailure, recordLocalAsrLifecycle } from '../localAsrLifecycle';
import { loadVoskRuntime } from './voskRuntime';
import { buildPhraseGrammar } from './voskGrammar';
import {
  biasedGraphCacheKey,
  unscopedBiasedGraphKey,
  createBiasedGraphRecognizer,
  getStoryGraphBytes,
  BiasedGraphUnsupportedError,
} from './voskBiasedGraph';

/**
 * Why a phrase decoded on the word-list grammar instead of its per-phrase biased HCLG.
 * All three are quality downgrades, so all three are reported as lifecycle failures —
 * `graphMissing` included, because an unpublished story genuinely decodes worse even
 * though it is the expected state before the LM pipeline has run for that story.
 *
 * `graphMissing` reports the state at ONE decode window, not the whole phrase: store
 * misses retry per window, so on a slow network an opening window can report missing
 * while the download is still in flight and a later window decodes biased. That upgrade
 * emits `graphs.biasedPickedUpMidPhrase` (see _reportBiasedPickup) — a `graphMissing`
 * with no matching pickup is the phrase that really did degrade throughout.
 */
const BIASED_FALLBACK_REASON = Object.freeze({
  RuntimeUnsupported: 'runtimeUnsupported',
  GraphMissing: 'graphMissing',
  BuildFailed: 'buildFailed',
});

const MS_PER_SEC = 1000;
const FREE_DECODE_KEY = '__free__';

/**
 * The claim slot a stream that overlaps another must pass (see _adoptStreamingWrap), and
 * the suffix that scopes its grammar-fallback key (see grammarCacheKey). Exported so the
 * module that BUILDS the key owns the vocabulary: the handler and the engine disagreeing on
 * this string fails silently in two directions — a mismatch between streamChunk and
 * streamFinish makes the intervention's flush destroy the phrase's committed segments, and
 * a value equal to DEFAULT_STREAM_SLOT makes the intervention's adopt release the phrase's
 * eviction protection. Neither reaches _decodeWindow's clobber canary.
 */
export const INTERVENTION_STREAM_SLOT = 'intervention';

/** Slot used by callers whose stream never overlaps another (the phrase, and warmup). */
const DEFAULT_STREAM_SLOT = 'default';

/** Every slot an overlapping caller may name. Mirrors BIASED_GRAPH_SCOPES: one registry, so
 *  construction and validation cannot drift. */
const STREAM_SLOTS = Object.freeze([INTERVENTION_STREAM_SLOT]);

/**
 * Rejects a slot that is not registered, which covers both silent failures in
 * INTERVENTION_STREAM_SLOT's note plus the typo case. DEFAULT_STREAM_SLOT is the one that
 * corrupts state — in streamChunk it releases the claim the overlapping stream was meant to
 * sit beside, in streamFinish it resolves the phrase's wrap and flushes its committed
 * segments away — but an unregistered slot silently mints a third cache entry and a claim
 * nothing releases, so both are refused. Omitting streamSlot is how a non-overlapping caller
 * opts into the default. Returns a rejected promise rather than throwing: these methods are
 * not async, so a throw would be synchronous and break any caller written as .catch().
 * @param {string} [slot]
 * @returns {Promise<never>|null}
 */
function badStreamSlot(slot) {
  if (slot === undefined || STREAM_SLOTS.includes(slot)) {
    return null;
  }
  return Promise.reject(new Error(`VoskAsr: unusable streamSlot '${slot}'`));
}


/**
 * Cache key for a grammar (or free-decode) recognizer, scoped by the caller's streaming
 * slot when one is named.
 *
 * The slot must scope this key exactly as the descriptor scope scopes the biased key: on a
 * story with no published graphs a phrase and its intervention both fall back here and can
 * compose an identical grammar string, and keyed on the grammar alone they would share one
 * wrap — where the intervention's reset flush destroys the phrase's committed segments,
 * silently (streaming never reaches _decodeWindow's clobber canary). Window decodes pass no
 * slot and share by design, since FinalResult resets state every window.
 * @param {string|null} grammar
 * @param {string} [streamSlot]
 * @returns {string}
 */
function grammarCacheKey(grammar, streamSlot) {
  const base = grammar || FREE_DECODE_KEY;
  return streamSlot ? `${base}:${streamSlot}` : base;
}
// Distinct biased-graph keys whose builds must fail — with NO biased decode ever
// having succeeded — before the failures are treated as systemic and biased decode
// is disabled for the session (see _maybeLatchSystemicBiasedFailure). Three is low
// enough that a runtime without the graph binding (a mislabeled vosk-v3 dir) latches
// within the first phrases instead of emitting a phraseFallback for every phrase in
// the story, and the zero-successes gate keeps scattered corrupt FSTs — a per-phrase
// problem — from ever tripping it.
//
// Counted in distinct GRAPHS, not distinct cache keys: _failedBiasedKeys stores SCOPED
// keys (see BIASED_GRAPH_SCOPE_INTERVENTION), so a phrase with an intervention would
// otherwise contribute two for one bad FST and trip this after two bad graphs.
// _maybeLatchSystemicBiasedFailure collapses the scope before counting.
const SYSTEMIC_BIASED_FAILURE_KEYS = 3;
// One shared LRU bound for every recognizer kind. Biased-graph recognizers had a
// separate, lower cap (2) when each owned a full per-phrase acoustic model in the
// worker (the v2 loadGraph path built a static-HCLG Model over the same AM, stacking
// acoustic models in the 32-bit WASM heap). On the vosk-v3 runtime a biased
// recognizer binds to the SHARED base acoustic model and owns only its own ~50 KB
// FST — no heavier than a grammar recognizer — so that cap's cost vanished and it
// was removed with it. Five is a TIGHT fit, not a bound with slack. The worst set enumerated
// below is a phrase boundary where both phrases intervene — biased_N, biased_N:iv, biased_N+1,
// biased_N+1:iv, and N+2's speculative warm — which is exactly five. _evictLru still finds a
// non-streaming victim there, but any sixth resident (a session warmup grammar, a
// free-decode entry, a :iv fallback grammar on an unpublished story) costs one eviction per
// boundary, which is the cost the bump removed. Six is the obvious lever at ~50 KB per v3
// recognizer; not taken on speculation — decode.interventionStats and decode.phraseStats
// carry queueDropCount and coldStages, so a real boundary eviction shows up there first.
// A story with no published graphs reaches the SAME size by a different route, and it does so
// because of the slot scoping added here: grammar_N, grammar_N:intervention, grammar_N+1,
// grammar_N+1:intervention plus the warmup grammar is five again. (An earlier version of this
// comment claimed the fallback case does not grow the set — wrong; scoping doubles its
// grammar entries.)
//
// Five is sufficient for both, not merely close: _ensureCacheCapacity evicts while
// `size >= MAX` BEFORE inserting, so five residents evict nothing — the first eviction needs a
// sixth. Six is the lever if one ever shows up, and it is now observable rather than guessed
// at (queueDropCount and coldStages ride on both decode.phraseStats and
// decode.interventionStats). Not taken on speculation: the only hypothetical sixth resident is
// a free-decode entry, which needs an empty word list — startIntervention refuses one, so it
// would have to come from a phrase with no expectedText. At four, the ordinary
// published-graph case already forced an eviction per intervention.
const MAX_CACHED_RECOGNIZERS = 5;
// Upper bound on a single window decode; guards against a wedged worker. Far above
// a real decode (tens of ms), so it never trips in normal operation.
//
// This is also the only staging bound left. The v2 loadGraph path had its own
// 20 s timeout because staging awaited a real worker round-trip (the per-phrase
// AM re-parse) BEFORE the decode timeout could arm. On vosk-v3 a graph-carrying
// create is fire-and-forget — the KaldiRecognizer constructor posts the create
// message and returns with no ack — so there is nothing to time out at staging;
// a wedged create surfaces on the first window's event wait, bounded here and
// classified as a create failure (_handleBiasedWrapFailure). Worker-side create
// failures that THROW arrive as recognizer-scoped `error` events instead of a
// rejected staging await — see the error listener in _wireRecognizer.
//
// NOT YET VALIDATED ON THE SLOWEST HARDWARE. Collapsing staging into this bound
// also collapsed the budget a COLD biased phrase gets: v2 allowed 20 s of graph
// load plus 8 s for the first window, where the worker's FIFO queue now makes the
// create work and the first acceptWaveform reply share this single 8 s. The
// consequence of being wrong also changed shape — a first-window timeout latches
// the key PERMANENTLY (nothing ever clears _failedBiasedKeys), is exempt from the
// teardown counter, and cannot reach the systemic latch once any phrase has
// decoded biased (_biasedEverDecoded), so a slow device produces a trickle of
// graphs.phraseFallback{buildFailed} and no session-level signal. Degradation is
// quality-only (the phrase decodes on the word-list grammar), which is why this
// stays as-is pending measurement rather than being padded on a guess: the
// outstanding on-device check must record how often the first window blows 8 s,
// not just whether it clears.
const DEFAULT_DECODE_TIMEOUT_MS = 8000;
const now = () => ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now());
const noop = () => undefined;

// The vosk worker persists extracted models in an Emscripten IDBFS mounted at
// /vosk (an IndexedDB database named after the mountpoint), keyed by a path
// derived from the model URL. Nothing in the runtime ever deletes sibling paths,
// and load() populates the ENTIRE mount into the worker's in-memory FS — so a
// model-URL change would permanently double storage, memory, and load time. We
// reset the database when the model URL changes; see resetStaleIdbfsModel.
//
// On the split bundle (vosk-v3) `modelUrl` is the ACOUSTIC archive at the
// runtime-independent asr-assets/model/ path, so the two events this reset used
// to conflate now separate cleanly: a graph revision bump changes only
// `graphUrl` — the worker's per-layer extraction markers replace just the
// graph/ tree inside the SAME keyed path, no reset needed or wanted — while a
// change to `modelUrl` itself (the one-time v2.x monolith -> AM-layer
// transition, or a future acoustic-model revision) still resets so the old
// tree cannot accumulate.
const VOSK_IDBFS_DB_NAME = '/vosk';
const VOSK_IDBFS_MODEL_URL_KEY = 'asrVoskIdbfsModelUrl';
// Upper bound on waiting for deleteDatabase: another tab's live vosk worker holds
// the DB open and fires onblocked; proceed rather than stall ASR init.
const IDBFS_RESET_TIMEOUT_MS = 1500;
// Upper bound on createModel (worker spawn + wasm instantiation + model
// download/extract-or-IDBFS-restore + Kaldi model construction). It exists ONLY
// to turn a hang into a rejection the handler can count — a worker whose /vosk
// open is queued behind a pending deleteDatabase (another tab holds the DB)
// would otherwise stall init forever. The cost of being wrong is one-sided, so
// bias LONG: a cold first install downloads the 42 MB archive inside the worker
// (a fresh network fetch if the blob worker's requests bypass the service
// worker), and on a slow school network that alone can take minutes; tripping
// the timeout there fails init for the session AFTER burning the download,
// while a real hang is detected just as well at 600 s as at 120 s.
const DEFAULT_MODEL_LOAD_TIMEOUT_MS = 600000;
// Fail-fast bound used ONLY when the IDBFS reset reported `blocked`: another
// tab's live connection holds /vosk, the pending (uncancellable) delete heads
// the connection queue, and the worker's open() will queue behind it until that
// tab closes — so the hang is predictable, not exotic, and the student is
// better served by the handler's fallback than by waiting out the long bound.
// A `timeout` outcome deliberately does NOT shorten: no onblocked means the
// delete is merely slow and will complete, after which the load pays a full
// re-extract — exactly the slow-hardware path the long default protects.
const BLOCKED_IDBFS_MODEL_LOAD_TIMEOUT_MS = 45000;

const VOSK_DEFAULTS = {
  sampleRate: 16000,
  windowMs: 1025,
  rollingStrideMs: 200,
  backend: 'vosk-wasm',
  modelVersion: null,
  runtimeUrl: null,
  modelUrl: null,
  wasmUrl: null,
  graphUrl: null,
  voskModule: null,
  audioContext: null,
  decodeTimeoutMs: DEFAULT_DECODE_TIMEOUT_MS,
  modelLoadTimeoutMs: DEFAULT_MODEL_LOAD_TIMEOUT_MS,
};

/** URL whose IDBFS transition this page session already attempted. Backstop for
 * environments where localStorage writes fail (Safari private mode): without it a
 * second createVoskAsr in the same page would queue a second deleteDatabase behind
 * the first model worker's open connection and stall its /vosk open. */
let sessionHandledIdbfsUrl = null;

/** Sticky for the page session once a reset observes `blocked`: the parked delete
 * keeps every later /vosk open queued too, but the marker/latch (correctly) skips
 * re-running the reset, so later createVoskAsr calls would never re-see the
 * signal and would fall back to the long bound for a hang we already predicted.
 * Cleared by any successful model load (a completed open proves the queue is
 * clear). Deliberately NOT persisted across page loads: after the blocker closes,
 * the completed delete forces a full 42 MB re-extract on the next load, and a
 * durable 45 s cap would turn that legitimate slow load into a failure loop on
 * the slowest devices. Worst case of forgetting is a background-only long wait. */
let sessionIdbfsDeleteBlocked = false;

/**
 * Deletes the worker's persisted model filesystem when the model URL changed since
 * the last load, so the old URL's extracted tree cannot accumulate (storage +
 * per-session memory + load time; see VOSK_IDBFS_DB_NAME).
 *
 * The marker means "transition to this URL ATTEMPTED" and is recorded BEFORE the
 * delete, on every outcome — never retried. A blocked/errored/timed-out delete
 * just leaves the old tree behind (the pre-fix status quo). Retrying would be
 * strictly worse: deleteDatabase cannot be cancelled, a blocked delete already
 * sits at the head of the /vosk connection queue stalling every later open, and a
 * timed-out delete usually completes anyway — a retry next session would wipe the
 * model extracted THIS session and re-download 42 MB in a silent loop.
 * Always best-effort — nothing here may block or fail ASR init.
 * @param {string} modelUrl
 * @returns {Promise<string|null>} The delete outcome ('success' | 'error' |
 *   'blocked' | 'timeout' | 'threw'), or null when no reset was attempted.
 *   Callers use 'blocked' to shorten the model-load bound: the worker's /vosk
 *   open is then predictably queued behind the pending delete.
 */
async function resetStaleIdbfsModel(modelUrl) {
  try {
    if (typeof indexedDB === 'undefined' || !modelUrl || sessionHandledIdbfsUrl === modelUrl) {
      return null;
    }
    sessionHandledIdbfsUrl = modelUrl;
    let previous = null;
    try {
      previous = localStorage.getItem(VOSK_IDBFS_MODEL_URL_KEY);
    } catch { /* treat unreadable storage as no record */ }
    if (previous === modelUrl) {
      return null;
    }
    try {
      localStorage.setItem(VOSK_IDBFS_MODEL_URL_KEY, modelUrl);
    } catch { /* private mode: the session latch above still bounds attempts */ }

    const outcome = await new Promise((resolve) => {
      let settled = false;
      const finish = (result) => {
        if (!settled) {
          settled = true;
          resolve(result);
        }
      };
      const timer = setTimeout(() => finish('timeout'), IDBFS_RESET_TIMEOUT_MS);
      let request;
      try {
        request = indexedDB.deleteDatabase(VOSK_IDBFS_DB_NAME);
      } catch {
        clearTimeout(timer);
        finish('threw');
        return;
      }
      request.onsuccess = () => { clearTimeout(timer); finish('success'); };
      request.onerror = () => { clearTimeout(timer); finish('error'); };
      // Blocked: another tab's worker holds the DB open. The delete stays pending
      // in the browser until that connection closes; don't stall init waiting.
      request.onblocked = () => { clearTimeout(timer); finish('blocked'); };
    });
    if (outcome === 'blocked') {
      sessionIdbfsDeleteBlocked = true;
    }
    logSessionInfo('voskAsr', { event: 'idbfsReset', outcome, fromUrl: previous, toUrl: modelUrl });
    return outcome;
  } catch {
    // Non-fatal by contract.
    return null;
  }
}

/** Test-only: clears the per-page-session IDBFS latches (module state that
 * otherwise leaks across tests, which share one module instance). */
export const __VOSK_ASR_TESTS__ = {
  resetIdbfsSessionState() {
    sessionHandledIdbfsUrl = null;
    sessionIdbfsDeleteBlocked = false;
  },
};

/** Bound on the read-only presence probe below — ONE deadline shared by the
 * `databases()` listing and the `open()` that may follow it. A /vosk open queued behind
 * another tab's pending delete never settles (the very hang
 * BLOCKED_IDBFS_MODEL_LOAD_TIMEOUT_MS exists for); the probe must answer 'unknown'
 * quickly rather than hold the loading gate. */
export const IDBFS_PROBE_TIMEOUT_MS = 1500;
/** Emscripten IDBFS stores one FILE_DATA record per file AND per directory under the
 * mount. A fully extracted small model is ~15 files + ~6 directories + 2 layer markers,
 * about 23 records. A mount holding only the graph layer (its ~5 files, directories and
 * marker) is about 9. The two misreads are NOT symmetric: cold-read-as-warm puts the
 * 45 s bound on a boot that must first download 22.8 MB and costs the session local ASR;
 * warm-read-as-cold costs only bar shape (the 'unknown' plan already accepts that shape).
 * So the threshold sits well above the graph-only count and below a full tree, erring
 * cold. The raw count is returned too (-> boot.started.idbfsEntries) so this is validated
 * from field data: a "warm" cohort with cold-shaped boot durations means it is wrong. */
export const IDBFS_WARM_MIN_ENTRIES = 16;
/** Emscripten's IDBFS object store name (fixed by the runtime, not by lexa-asr-assets). */
const IDBFS_STORE_NAME = 'FILE_DATA';

/** Answers for probeVoskIdbfsModelPresence. Mirrors asr/localAsrBoot's
 * LOCAL_ASR_MODEL_PRESENCE; duplicated as string literals so this module keeps its
 * dependency direction (voskAsr does not import the boot planner). Pinned equal in
 * voskAsr.test.js. */
const PRESENCE = Object.freeze({ WARM: 'warm', COLD: 'cold', UNKNOWN: 'unknown' });
__VOSK_ASR_TESTS__.PRESENCE = PRESENCE;

const presenceAnswer = (presence, entries = null) => ({ presence, entries });

/**
 * Whether the worker's persisted model tree for `modelUrl` is already in IDBFS, i.e.
 * whether the coming createModel is a warm restore or a cold 22.8 MB download (TT-1313 /
 * CR-10679). App uses the answer to size the gated boot's estimated progress bar and
 * pick its hard timeout, and logs it as the cold/warm proxy the rollout analyzer keys on.
 *
 * Strictly read-only and best-effort:
 * - never CREATES the database: prefers `indexedDB.databases()` where available, and
 *   when it is not, aborts the versionchange transaction an `upgradeneeded` would open
 *   (upgradeneeded on a plain open means the db did not exist);
 * - never blocks the caller: ONE deadline of IDBFS_PROBE_TIMEOUT_MS across the listing
 *   and the open, and every error path answers 'unknown' rather than guessing;
 * - never leaks a connection: an open that lands after the deadline is closed on
 *   arrival, since a live main-thread handle is exactly what makes a later
 *   `deleteDatabase` (resetStaleIdbfsModel, or another tab's) report `blocked`;
 * - answers 'cold' without touching IndexedDB when the recorded model URL differs from
 *   `modelUrl`, because resetStaleIdbfsModel is about to delete the tree anyway.
 * @param {string} modelUrl The acoustic archive URL createModel will be given.
 * @returns {Promise<{presence: 'warm'|'cold'|'unknown', entries: number|null}>}
 *   `entries` is the raw FILE_DATA record count when the store was actually read.
 */
export async function probeVoskIdbfsModelPresence(modelUrl) {
  try {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      return presenceAnswer(PRESENCE.UNKNOWN);
    }

    let previous = null;
    try {
      previous = localStorage.getItem(VOSK_IDBFS_MODEL_URL_KEY);
    } catch { /* unreadable storage: fall through to the db itself */ }
    if (modelUrl && previous && previous !== modelUrl) {
      return presenceAnswer(PRESENCE.COLD);
    }

    const deadlineAt = Date.now() + IDBFS_PROBE_TIMEOUT_MS;
    const remainingMs = () => Math.max(0, deadlineAt - Date.now());

    if (typeof indexedDB.databases === 'function') {
      let listingTimer = null;
      const listed = await Promise.race([
        Promise.resolve(indexedDB.databases()).catch(() => null),
        new Promise((resolve) => {
          listingTimer = setTimeout(() => resolve(undefined), remainingMs());
        }),
      ]);
      clearTimeout(listingTimer);
      if (listed === undefined) {
        return presenceAnswer(PRESENCE.UNKNOWN);
      }
      if (Array.isArray(listed) && !listed.some((db) => db && db.name === VOSK_IDBFS_DB_NAME)) {
        return presenceAnswer(PRESENCE.COLD);
      }
      // A rejected databases() (null) falls through to the open below.
    }

    return await new Promise((resolve) => {
      let settled = false;
      let db = null;
      const closeDb = (handle) => {
        if (handle && typeof handle.close === 'function') {
          try { handle.close(); } catch { /* already closed */ }
        }
      };
      const finish = (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        closeDb(db);
        resolve(result);
      };
      const timer = setTimeout(() => finish(presenceAnswer(PRESENCE.UNKNOWN)), remainingMs());

      let request;
      try {
        request = indexedDB.open(VOSK_IDBFS_DB_NAME);
      } catch {
        finish(presenceAnswer(PRESENCE.UNKNOWN));
        return;
      }
      request.onupgradeneeded = (event) => {
        // The db did not exist (or is older than the runtime's schema). Do not let this
        // open create/upgrade it — that is the worker's job, at the worker's version.
        try { event?.target?.transaction?.abort(); } catch { /* best effort */ }
        finish(presenceAnswer(PRESENCE.COLD));
      };
      request.onerror = () => finish(presenceAnswer(PRESENCE.UNKNOWN));
      request.onblocked = () => finish(presenceAnswer(PRESENCE.UNKNOWN));
      request.onsuccess = () => {
        if (settled) {
          // Landed after the deadline: the answer is already 'unknown'; just do not leak.
          closeDb(request.result);
          return;
        }
        db = request.result;
        try {
          if (!db || !db.objectStoreNames || !db.objectStoreNames.contains(IDBFS_STORE_NAME)) {
            finish(presenceAnswer(PRESENCE.COLD));
            return;
          }
          const countRequest = db.transaction(IDBFS_STORE_NAME, 'readonly')
            .objectStore(IDBFS_STORE_NAME)
            .count();
          countRequest.onsuccess = () => {
            const entries = typeof countRequest.result === 'number' ? countRequest.result : 0;
            finish(presenceAnswer(entries >= IDBFS_WARM_MIN_ENTRIES ? PRESENCE.WARM : PRESENCE.COLD, entries));
          };
          countRequest.onerror = () => finish(presenceAnswer(PRESENCE.UNKNOWN));
        } catch {
          finish(presenceAnswer(PRESENCE.UNKNOWN));
        }
      };
    });
  } catch {
    return presenceAnswer(PRESENCE.UNKNOWN);
  }
}

/**
 * Bounds a model-load promise; on timeout rejects, and frees the model's Kaldi
 * allocations if the load ever settles late (Model.terminate() only posts a
 * message to the worker — the worker thread itself survives). On the true hang
 * path this timeout exists for — the worker's /vosk open queued behind a pending
 * delete — the promise never settles, so nothing is (or can be) cleaned up here.
 * @param {Promise<object>} modelPromise
 * @param {number} timeoutMs
 * @param {string} [context] Appended to the rejection message (field correlation).
 * @returns {Promise<object>}
 */
function withModelLoadTimeout(modelPromise, timeoutMs, context = '') {
  if (!timeoutMs) {
    return modelPromise;
  }
  return new Promise((resolve, reject) => {
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      reject(new Error(`createVoskAsr: model load timed out after ${timeoutMs}ms${context}`));
    }, timeoutMs);
    modelPromise.then(
      (model) => {
        clearTimeout(timer);
        if (timedOut) {
          try {
            if (model && typeof model.terminate === 'function') {
              model.terminate();
            }
          } catch { /* best-effort release of the late model's allocations */ }
          return;
        }
        resolve(model);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Loads the Vosk runtime and model, then returns an Asr-compatible instance.
 * @param {Partial<typeof VOSK_DEFAULTS>} [opts]
 * @returns {Promise<VoskAsr>}
 */
export async function createVoskAsr(opts = {}) {
  const settings = { ...VOSK_DEFAULTS, ...opts };
  const resetOutcome = await resetStaleIdbfsModel(settings.modelUrl);
  // `blocked` — seen now or earlier this page session (carryover) — predicts the
  // hang (see BLOCKED_IDBFS_MODEL_LOAD_TIMEOUT_MS): fail fast to the handler's
  // fallback. Math.min keeps explicit caller overrides that are already shorter;
  // every other outcome keeps the long default.
  const blocked = resetOutcome === 'blocked' || sessionIdbfsDeleteBlocked;
  const modelLoadTimeoutMs = blocked
    ? Math.min(settings.modelLoadTimeoutMs || BLOCKED_IDBFS_MODEL_LOAD_TIMEOUT_MS, BLOCKED_IDBFS_MODEL_LOAD_TIMEOUT_MS)
    : settings.modelLoadTimeoutMs;
  const timeoutContext = blocked && resetOutcome !== 'blocked'
    ? ' (idbfsReset=blocked-carryover)'
    : (resetOutcome ? ` (idbfsReset=${resetOutcome})` : '');
  const vosk = settings.voskModule || await loadVoskRuntime(settings.runtimeUrl);
  // wasmUrl is REQUIRED for the split runtime's .br adoption: the worker's
  // built-in fallback fetches a sibling literally named `vosk.wasm` next to the
  // model archive, which can never match the precompressed `vosk.wasm.br` cache
  // entry.
  //
  // graphUrl selects the split bundle (vosk-v3): modelUrl is then the stable
  // acoustic archive and graphUrl the per-revision decoding-graph archive; the
  // worker extracts both into one IDBFS tree keyed on modelUrl ALONE, each layer
  // gated by its own marker — which is exactly what lets the extracted acoustic
  // tree survive graph revision bumps (see resetStaleIdbfsModel, which
  // deliberately needs no change for a graph bump). The v3 runtime REQUIRES it:
  // the acoustic archive ships no decoding graph and a graphless load fails
  // loudly. It is only absent for injected test runtimes over monolithic bundles
  // — pre-v3 workers ignore the extra argument.
  const model = await withModelLoadTimeout(
    vosk.createModel(settings.modelUrl, 0, settings.wasmUrl, settings.graphUrl || undefined),
    modelLoadTimeoutMs,
    timeoutContext,
  );
  // A completed load proves the /vosk queue is clear; drop the carryover cap.
  sessionIdbfsDeleteBlocked = false;
  return new VoskAsr(model, settings);
}

class VoskAsr {
  /**
   * @param {object} model vosk-browser model (with `KaldiRecognizer`, `terminate`).
   * @param {typeof VOSK_DEFAULTS} settings
   */
  constructor(model, settings) {
    this._model = model;
    this.sampleRate = settings.sampleRate;
    this.windowMs = settings.windowMs;
    this.backend = settings.backend;
    this.modelVersion = settings.modelVersion;
    // Capability marker read by LocalPlacementAsrHandler._supportsBiasedGraph to
    // gate biased-descriptor threading (the NeMo backend has no such property).
    // Deliberately true even when the RUNTIME can't decode biased graphs (an
    // injected pre-v3 runtime without a graphUrl): threading the descriptor is
    // what produces the once-per-session `graphs.biasedDecodeUnavailable`
    // telemetry a capability-gated caller would silently skip.
    this.supportsBiasedGraphs = true;
    this._engine = { windowSamples: Math.round((settings.windowMs * settings.sampleRate) / MS_PER_SEC) };
    this.decode = Object.freeze({
      streaming: Object.freeze({ rollingStrideMs: settings.rollingStrideMs }),
      consensus: Object.freeze({}),
    });
    this._recognizers = new Map();
    // Biased-graph keys whose recognizer BUILD failed (graph-create error): the
    // phrase permanently falls back to the word-list grammar instead of re-paying a
    // doomed graph load on every 200ms window. Store misses are NOT recorded here —
    // they retry per window (a cheap Map lookup) so a phrase whose download finishes
    // mid-phrase picks its graph up on the next window.
    this._failedBiasedKeys = new Set();
    this._warnedBiasedKeys = new Set();
    // Keys whose ONLY fallback report so far was a speculative (endPhrase,
    // phraseIndex + 1) warm failure, labeled `warm: true`. If the student then
    // actually reads the phrase, that label becomes wrong — the shared per-key
    // latch would otherwise leave the speculative event as the phrase's only
    // record, and coverage reads that exclude warm-labeled events would
    // systematically undercount real degradation. The first live encounter emits
    // a corrective report and retires the key (see _correctSpeculativeWarmReport,
    // the same shape as _reportBiasedPickup's graphMissing correction).
    this._warmOnlyWarnedKeys = new Set();
    // Keys reported as graphMissing whose graph could still arrive: a store miss retries
    // per window, so a download landing mid-phrase upgrades that phrase to biased decode.
    // Tracked so the upgrade gets a correction event (see _reportBiasedPickup) instead of
    // leaving the graphMissing report standing as if the whole phrase had degraded.
    this._pendingBiasedPickupKeys = new Set();
    this._loggedGraphLoadUnsupported = false;
    // Latch for the once-per-session warning when a window decode lands on a
    // recognizer a streaming phrase owns (see _warnStreamingClobber).
    this._warnedStreamingClobber = false;
    // The recognizers live streams currently own, keyed by slot (see
    // _adoptStreamingWrap). The phrase and a mid-phrase intervention hold claims
    // SIMULTANEOUSLY on different recognizers — their lifecycles overlap, though
    // their chunks never do (the caller stops phrase listening before the
    // intervention's starts, and _tail serializes every decode regardless) — so
    // one tracking reference cannot represent the claims: when it did, the
    // intervention's adopt released the phrase's claim, stripping the
    // mid-utterance phrase recognizer of its _evictLru protection — cache
    // pressure then silently discarded the phrase transcript, the exact failure
    // the recognizer isolation exists to prevent.
    this._streamingWraps = new Map();
    // Whether the loaded runtime can bind a per-recognizer biased graph. Not
    // detectable from the model object (a v2.x Model exposes the same surface),
    // so it is derived from the resolved manifest: a split-bundle `graphUrl`
    // means the vosk-v3 runtime, the only one with the one-step graph create.
    // On v2.x this must stay false even though their Model.loadGraph exists —
    // that path re-parses the acoustic model per swap and strands ~33 MB of
    // WASM heap each time (lexa-asr-assets#33). This is a CONTRACT ASSUMPTION,
    // not a capability probe — a mislabeled runtime (the vosk-v3 dir somehow
    // serving a build without the binding) is caught by the systemic-failure
    // latch below (_maybeLatchSystemicBiasedFailure) rather than here.
    this._biasedGraphRuntime = Boolean(settings.graphUrl);
    // Latched once the biased path throws BiasedGraphUnsupportedError, or once
    // build failures look systemic (see _maybeLatchSystemicBiasedFailure). The
    // capability is constant for the model's life, so after the first miss we stop
    // entering the biased path entirely — otherwise every ~200ms decode window
    // would construct and throw a BiasedGraphUnsupportedError for the whole session.
    this._graphLoadUnsupported = false;
    // Whether ANY biased recognizer has produced a worker reply this session —
    // proof the runtime genuinely has the graph binding. Gates the systemic latch:
    // once one biased decode worked, later build failures are per-phrase problems
    // (a corrupt FST), never grounds to disable biased decode session-wide.
    this._biasedEverDecoded = false;
    this._decodeTimeoutMs = settings.decodeTimeoutMs;
    this._disposing = false;
    // Monotonic count of recognizers CONSTRUCTED (every path goes through
    // _wireRecognizer). Decodes sample it around _resolveRecognizer to report whether
    // they paid a cold stage — a biased-graph create or a grammar compose — or hit
    // the cache. Without it a decode that waited on staging is indistinguishable
    // from one that waited on the decode itself. NOTE for field triage: #5878's
    // stageMs baselines (84.6 ms median staged) were measured on the v2.2 loadGraph
    // path and are further contaminated by its per-swap heap leak
    // (lexa-asr-assets#33); v3 numbers are not comparable to them.
    this._recognizerBuilds = 0;
    // Serializes decodes; each transcribe chains onto the previous one.
    this._tail = Promise.resolve();
    // Wraps Float32 PCM in the AudioBuffer acceptWaveform expects. Absent (tests),
    // PCM is passed through and the runtime coerces it.
    this._bufCtx = settings.audioContext || makeBufferContext(settings.sampleRate);
  }

  /**
   * Decodes one window and returns the phrase-constrained transcript. Serialized
   * behind any in-flight decode. Rejects on a wedged-worker timeout so the handler
   * counts an inference failure.
   * @param {Float32Array} input
   * @param {{wordList?: string[], graph?: string|string[], biasedGraph?: object}} [opts]
   * @returns {Promise<{text: string, words: string[], wordScores: Float32Array|undefined, timings: object}>}
   */
  transcribe(input, opts = {}) {
    const run = () => this._transcribeNow(input, opts);
    const result = this._tail.then(run, run);
    // Keep the queue alive regardless of this decode's outcome.
    this._tail = result.then(noop, noop);
    return result;
  }

  /**
   * @param {Float32Array} input
   * @param {object} opts
   * @returns {Promise<object>}
   */
  async _transcribeNow(input, opts) {
    if (!this._model || this._disposing) {
      throw new Error('VoskAsr: transcribe called after dispose');
    }
    const staging = this._beginStaging();
    const wrap = await this._resolveRecognizer(opts);
    const staged = this._endStaging(staging);
    const t0 = now();
    const { text, wordScores } = await this._decodeWindow(wrap, toFloat32(input));
    return {
      text,
      words: splitWords(text),
      wordScores,
      // timeoutMs rides along so the handler's per-phrase stats can count decodes
      // that graze the budget without hard-coding a second copy of the constant.
      // ...staged leads so a future _endStaging field can never shadow the real
      // total/timeoutMs measurements the consumer keys its near-miss math on.
      timings: { ...staged, total: now() - t0, timeoutMs: this._decodeTimeoutMs },
    };
  }

  /**
   * Streaming decode: feeds ONE stride of new audio to the phrase's persistent
   * recognizer and returns the cumulative transcript (committed endpoint segments +
   * live partial). Unlike transcribe(), nothing is finalized and no audio is ever
   * re-decoded — each sample is heard exactly once (~5x less acoustic compute than
   * the 1025ms-window-per-200ms-stride pattern). Serialized on the same queue as
   * transcribe. `opts.reset` (first chunk of a phrase) flushes any stale decoder
   * state left on a cached recognizer from an earlier use. `opts.streamSlot` names
   * the streaming-claim slot for callers whose stream overlaps another (a
   * mid-phrase intervention beside its phrase) — see _adoptStreamingWrap.
   * @param {Float32Array} input new samples only (a stride, not a window)
   * @param {{wordList?: string[], biasedGraph?: object, reset?: boolean, streamSlot?: string}} [opts]
   * @returns {Promise<{text: string, words: string[], wordScores: Float32Array|undefined, timings: object}>}
   */
  streamChunk(input, opts = {}) {
    const slotError = badStreamSlot(opts.streamSlot);
    if (slotError) {
      return slotError;
    }
    const run = () => this._streamChunkNow(input, opts);
    const result = this._tail.then(run, run);
    this._tail = result.then(noop, noop);
    return result;
  }

  /**
   * Ends a streaming phrase: flushes the final segment, resets the recognizer's
   * stream state, and returns the full transcript. Serialized behind in-flight work.
   * Flushes an ALREADY-CACHED recognizer only, never building one for a phrase that
   * has ended (see _streamFinishNow); an empty transcript comes back when there was
   * nothing cached to flush. Callers that streamed with a `streamSlot` must pass
   * the same slot here so the grammar-fallback lookup resolves THEIR recognizer,
   * not another stream's identically-worded one.
   * @param {{wordList?: string[], biasedGraph?: object, streamSlot?: string}} [opts]
   * @returns {Promise<{text: string, words: string[], wordScores: Float32Array|undefined}>}
   */
  streamFinish(opts = {}) {
    const slotError = badStreamSlot(opts.streamSlot);
    if (slotError) {
      return slotError;
    }
    const run = () => this._streamFinishNow(opts);
    const result = this._tail.then(run, run);
    this._tail = result.then(noop, noop);
    return result;
  }

  /** @private */
  async _streamChunkNow(input, opts) {
    if (!this._model || this._disposing) {
      throw new Error('VoskAsr: streamChunk called after dispose');
    }
    const staging = this._beginStaging();
    let wrap = await this._resolveRecognizer(opts);
    // A phrase-open reset that resolves a recognizer which has ALREADY produced a
    // reply cannot reuse it: vosk exposes no reset/reinit (only remove), so the
    // first acceptWaveform on a finalized recognizer faults with "null function"
    // (TT-1237). Skipping the reset flush's retrieveFinalResult (the pendingUtterance
    // guard below) removes the no-op double-finalize but NOT this fault — the accept
    // that follows still lands on a finalized recognizer. Rebuild it so the live
    // utterance decodes on a clean one. This is the shared-key reuse shape: pre-story
    // warmup -> live phrase 0 (warmup finalizes phrase 0's biased wrap), and any
    // same-key re-read (ReaderManager.tryAgain re-arming the same phraseIndex, a
    // mid-read graph swap). Cheap and warmth-preserving — only the per-phrase graph
    // resolve repeats (a few ms), while the JIT and acoustic model are process-global
    // and stay warm. The engine already recovers from this reactively (evict on the
    // failed decode, rebuild next window); doing it here just spares the one wasted
    // window and the spurious failure log.
    if (opts.reset && wrap.everReplied) {
      this._evict(wrap);
      wrap = await this._resolveRecognizer(opts);
    }
    const staged = this._endStaging(staging);
    const t0 = now();
    let flushMs = 0;
    if (opts.reset) {
      // Flush stale utterance state from a cached recognizer's earlier use, then
      // discard whatever it committed — the new phrase starts from empty. The flush
      // spends its own decode-timeout wait INSIDE the t0..total span, so a reset stride's
      // total covers two separately-bounded regions (flush + accept). Report the flush
      // portion as flushMs so a consumer can measure the accept alone rather than guess
      // the split from a boolean.
      const flushStart = now();
      await this._flushStream(wrap);
      flushMs = now() - flushStart;
      wrap.results.length = 0;
      wrap.partial = '';
    }
    // Claim the streaming slot AFTER any reset flush (which releases it). A failed
    // accept below evicts the wrap, and _evict releases the claim with it.
    this._adoptStreamingWrap(wrap, opts.streamSlot);
    wrap.events = 0;
    wrap.waiter = null;
    try {
      // One reply per acceptWaveform: `result` on an endpoint, else `partialresult`.
      wrap.rec.acceptWaveform(this._toAudioBuffer(toFloat32(input)));
      // Audio is now pending a finalize; the next _flushStream must run rather than
      // skip as a no-op double-finalize (see _flushStream, TT-1237).
      wrap.pendingUtterance = true;
      await waitForEvents(wrap, 1, this._decodeTimeoutMs);
    } catch (err) {
      // A TIMEOUT on a biased recognizer that never replied is a create failure
      // that never surfaced (a wedged create posts no error event) — latch it so
      // the next window falls back to the grammar instead of rebuilding the same
      // doomed recognizer every stride. See _handleBiasedWrapFailure.
      //
      // Gated on `decodeTimeout` because this try also covers the SYNCHRONOUS
      // acceptWaveform/retrieveFinalResult calls above it. A client-side throw
      // there is a bug in this code, not a worker create failure: classifying it
      // would permanently latch a phrase whose graph was never tested, exempt the
      // failure from the teardown counter, and — at three such faults — log
      // biasedDecodeUnavailable{repeatedBuildFailures}, pointing triage at the CDN.
      // A worker `error` event needs no branch here: the listener classifies before
      // it rejects this waiter, so skipping it also drops a redundant second call.
      if (err.decodeTimeout) {
        this._handleBiasedWrapFailure(wrap, err);
      }
      this._evict(wrap);
      throw err;
    }
    // flushMs (0 on a non-reset stride) lets the consumer subtract the phrase-open flush
    // and measure the accept against a single timeout-bounded budget. ...staged leads so it
    // can never shadow total/timeoutMs/flushMs.
    return { ...streamResult(wrap), timings: { ...staged, total: now() - t0, timeoutMs: this._decodeTimeoutMs, flushMs } };
  }

  /**
   * Opens a staging measurement around _resolveRecognizer.
   *
   * `total` in the returned timings deliberately covers the DECODE only, so a decode
   * that spent seconds staging a biased graph reported the same `total` as a fast one —
   * the cost was real but attributed nowhere. These two helpers split it out.
   * @returns {{at: number, builds: number}}
   * @private
   */
  _beginStaging() {
    return { at: now(), builds: this._recognizerBuilds };
  }

  /**
   * Closes a staging measurement.
   * @param {{at: number, builds: number}} staging
   * @returns {{stageMs: number, staged: boolean}} `staged` is true when resolution
   * CONSTRUCTED a recognizer rather than hitting the cache — a biased-graph create
   * over the shared acoustic model (vosk-v3, ~tens of ms), or a word-list grammar
   * composed against the lookahead model. Either is a per-phrase cost paid before any
   * audio is decoded, which is what separates a stalled phrase from a uniformly slow
   * decoder.
   * @private
   */
  _endStaging(staging) {
    return {
      stageMs: Math.round(now() - staging.at),
      staged: this._recognizerBuilds > staging.builds,
    };
  }

  /**
   * Marks `wrap` as the recognizer the stream in `slot` owns, releasing whatever held that
   * slot before.
   *
   * Slots exist because a phrase and a mid-phrase intervention hold claims at once (their
   * lifecycles overlap; their chunks never do, since _tail serializes every decode) and must
   * not release each other's — doing so stripped the mid-utterance phrase recognizer of its
   * _evictLru protection. The phrase and warmup share the default slot on purpose: on the
   * mainline path they resolve the same recognizer, so phrase 0's adopt supersedes an
   * abandoned warmup claim.
   *
   * The engine owns this rather than trusting callers to close every phrase, because two
   * ordinary cases leave a claim behind: a phrase that ends with its opening decode still
   * queued runs no finish, and a phrase whose graph lands mid-read switches recognizers
   * without one. An abandoned claim is not a transcript risk, but it is passed over by
   * _evictLru and it spends the once-per-session clobber warning.
   * @param {object} wrap
   * @param {string} [slot] Claim slot; omit when the stream never overlaps another.
   * @returns {void}
   * @private
   */
  _adoptStreamingWrap(wrap, slot = DEFAULT_STREAM_SLOT) {
    const previous = this._streamingWraps.get(slot);
    if (previous && previous !== wrap) {
      this._streamingWraps.delete(slot);
      // Backstop: key scoping should keep distinct slots on distinct wraps, but if that
      // ever regresses the flag must survive while another slot still claims this wrap.
      if (!this._isStreamingClaimed(previous)) {
        previous.streaming = false;
      }
    }
    this._streamingWraps.set(slot, wrap);
    wrap.streaming = true;
  }

  /**
   * Whether any slot currently claims `wrap`.
   * @param {object} wrap
   * @returns {boolean}
   * @private
   */
  _isStreamingClaimed(wrap) {
    for (const held of this._streamingWraps.values()) {
      if (held === wrap) {
        return true;
      }
    }
    return false;
  }

  /**
   * Drops `wrap`'s claim on every streaming slot holding it: the flag, plus the
   * tracking references (so a discarded recognizer is not kept alive by them).
   * @param {object} wrap
   * @returns {void}
   * @private
   */
  _releaseStreamingWrap(wrap) {
    wrap.streaming = false;
    for (const [slot, held] of this._streamingWraps) {
      if (held === wrap) {
        this._streamingWraps.delete(slot);
      }
    }
  }

  /** @private */
  async _streamFinishNow(opts) {
    if (!this._model || this._disposing) {
      throw new Error('VoskAsr: streamFinish called after dispose');
    }
    const wrap = this._findCachedRecognizer(opts);
    if (!wrap) {
      // Cache-hit only, deliberately. Resolving normally here would BUILD a
      // recognizer for a phrase that has just ENDED, serialized on _tail ahead of
      // the next phrase's opening decodes, and its _ensureCacheCapacity would evict
      // a recognizer still wanted. If nothing was ever built for this phrase, no
      // audio ever reached a recognizer, so there is by definition no utterance
      // state to flush.
      return { text: '', words: [], wordScores: undefined };
    }
    await this._flushStream(wrap);
    const final = streamResult(wrap);
    wrap.results.length = 0;
    return final;
  }

  /**
   * Returns the already-cached recognizer these opts would resolve to, without ever
   * building one. Mirrors _resolveRecognizer's preference order — the phrase's biased
   * graph first, then the word-list/graph text grammar — so it finds whichever
   * recognizer the decodes actually ran on. Null when neither is cached.
   * @param {{wordList?: string[], graph?: string|string[], biasedGraph?: object,
   *          streamSlot?: string}} opts
   * @returns {object|null}
   * @private
   */
  _findCachedRecognizer(opts) {
    if (opts.biasedGraph) {
      const biased = this._touchRecognizer(biasedGraphCacheKey(opts.biasedGraph));
      if (biased) {
        return biased;
      }
    }
    const grammar = buildPhraseGrammar(opts.wordList, opts.graph);
    return this._touchRecognizer(grammarCacheKey(grammar, opts.streamSlot));
  }

  /**
   * Retrieves (and keeps) the final segment, resetting Kaldi's utterance state —
   * the same reset-by-FinalResult contract _decodeWindow relies on per window.
   * @param {object} wrap
   * @returns {Promise<void>}
   * @private
   */
  async _flushStream(wrap) {
    // Both callers flush at an utterance boundary (reset at phrase start, finish at
    // phrase end), so the recognizer is no longer mid-phrase either way.
    this._releaseStreamingWrap(wrap);
    wrap.events = 0;
    wrap.waiter = null;
    // Skip a retrieveFinalResult that follows another with no acceptWaveform between
    // (a flush after a flush). The recognizer is already finalized, so the second
    // FinalResult decodes nothing — and on the vosk worker that no-op double-finalize
    // faults with "null function" (TT-1237). The shared-key reuse shape (warmup ->
    // live phrase 0, a same-key tryAgain re-read, a mid-read graph swap) is handled
    // upstream in _streamChunkNow, which rebuilds an already-replied recognizer at
    // phrase open rather than reusing a finalized one — vosk has no reset, so reuse
    // faults on the following acceptWaveform too, not just on the double-finalize. This
    // guard remains for the phrase-END flush (streamFinish): a finish with nothing
    // accepted since the last finalize (e.g. a cancelled-then-finished warmup, or a
    // finish after a finish) must not fault. A live-audio flush is unaffected —
    // acceptWaveform sets the flag back on — so the cancelled-warmup shape (accumulated
    // audio never flushed) still finalizes exactly once here.
    if (!wrap.pendingUtterance) {
      return;
    }
    try {
      wrap.rec.retrieveFinalResult();
      await waitForEvents(wrap, 1, this._decodeTimeoutMs);
      wrap.pendingUtterance = false;
    } catch (err) {
      // A TIMEOUT on a biased recognizer that never replied is a create failure
      // that never surfaced (a wedged create posts no error event) — latch it so
      // the next window falls back to the grammar instead of rebuilding the same
      // doomed recognizer every stride. See _handleBiasedWrapFailure.
      //
      // Gated on `decodeTimeout` because this try also covers the SYNCHRONOUS
      // acceptWaveform/retrieveFinalResult calls above it. A client-side throw
      // there is a bug in this code, not a worker create failure: classifying it
      // would permanently latch a phrase whose graph was never tested, exempt the
      // failure from the teardown counter, and — at three such faults — log
      // biasedDecodeUnavailable{repeatedBuildFailures}, pointing triage at the CDN.
      // A worker `error` event needs no branch here: the listener classifies before
      // it rejects this waiter, so skipping it also drops a redundant second call.
      if (err.decodeTimeout) {
        this._handleBiasedWrapFailure(wrap, err);
      }
      this._evict(wrap);
      throw err;
    }
  }

  /**
   * Chooses the recognizer for this decode. When `opts.biasedGraph` is present the
   * per-story biased HCLG is preferred. Every biased problem detectable AT
   * RESOLUTION — runtime without the graph binding, bytes missing from the
   * per-story store, a synchronous graph-create failure — falls back to the
   * word-list/graph text grammar for this window, with a log so the degradation is
   * visible. The one biased failure that CAN fail a decode is a worker-side create
   * failure, which on v3 arrives asynchronously (no create ack to reject): it fails
   * at most that one window per phrase key — the key latches and the next window
   * resolves the grammar — and its rejection is marked `biasedQualityFallback` so
   * the handler logs it WITHOUT counting it toward the consecutive-failure
   * teardown. Either way a biased failure stays a quality downgrade: it can never
   * abandon local ASR for the session over a bad 50KB file.
   * @param {object} opts
   * @returns {Promise<object>}
   */
  async _resolveRecognizer(opts) {
    if (opts.biasedGraph && !this._graphLoadUnsupported) {
      try {
        const wrap = await this._getBiasedGraphRecognizer(opts.biasedGraph);
        if (wrap) {
          this._reportBiasedPickup(opts.biasedGraph);
          return wrap;
        }
        // Null means the bytes are not in the store, or this key's graph is already known to
        // fail. This used to be entirely silent: a session decoding every phrase on the
        // word-list grammar because no graphs were published looked identical in the logs to
        // one decoding against real biased graphs, which is exactly the difference that
        // explains a phantom-skip complaint. Reported per phrase (the shared per-key latch
        // keeps an already-reported build failure from double-counting) rather than per window.
        //
        // The two causes must not share a reason. A store miss is `expected: true` — the
        // ordinary state before the LM pipeline has run for a story — while a known-bad graph
        // is not. Before _isBiasedKeyFailed consulted the base key, an intervention whose
        // PHRASE graph had failed built and failed on its own, reporting buildFailed under its
        // scope; now its build is skipped, so without this it would report the corrupt graph as
        // an expected unpublished-graph miss.
        this._reportBiasedFallback(
          this._isBiasedKeyFailed(biasedGraphCacheKey(opts.biasedGraph))
            ? BIASED_FALLBACK_REASON.BuildFailed
            : BIASED_FALLBACK_REASON.GraphMissing,
          opts.biasedGraph,
        );
      } catch (err) {
        if (err instanceof BiasedGraphUnsupportedError) {
          // Runtime can't load graphs; latch so later windows skip the biased path
          // instead of re-throwing this every decode.
          this._graphLoadUnsupported = true;
          this._reportBiasedFallback(BIASED_FALLBACK_REASON.RuntimeUnsupported, opts.biasedGraph, err);
        } else {
          this._reportBiasedFallback(BIASED_FALLBACK_REASON.BuildFailed, opts.biasedGraph, err);
        }
      }
    }
    const grammar = buildPhraseGrammar(opts.wordList, opts.graph);
    return this._getRecognizer(grammar, opts.streamSlot);
  }

  /**
   * Reports that a phrase fell back to the word-list grammar, to the console and to the
   * local ASR lifecycle timeline, without spamming the per-window decode loop: the
   * runtime capability gap once per session, everything else once per phrase key.
   *
   * The per-key latch is deliberately shared across reasons so a phrase that reports a
   * build failure is not reported a second time as a store miss on the next window
   * (`_failedBiasedKeys` makes every later lookup for that key return null).
   * @param {string} reason One of BIASED_FALLBACK_REASON.
   * @param {object} descriptor `{ storyId, phraseIndex }`
   * @param {Error} [err]
   * @param {boolean} [warm] The failure came from a SPECULATIVE warm (the
   *   endPhrase warm targets `phraseIndex + 1`, a phrase the student may never
   *   read — a startPhrase warm is NOT speculative, its phrase is being read),
   *   carried as `warm: true` in the payload so per-phrase biased-coverage reads
   *   can exclude phrases that never actually decoded degraded. If the phrase IS
   *   read later, _correctSpeculativeWarmReport emits the unlabeled record — with
   *   two documented escapes, see its docblock.
   * @returns {void}
   */
  _reportBiasedFallback(reason, descriptor, err, warm = false) {
    if (reason === BIASED_FALLBACK_REASON.RuntimeUnsupported) {
      if (this._loggedGraphLoadUnsupported) {
        return;
      }
      this._loggedGraphLoadUnsupported = true;
      // A runtime without the graph binding is a fixed property of the build, not an anomaly, so
      // it is recorded as a known-cause degradation. The recorder writes the console line.
      recordLocalAsrFailure(
        'graphs.biasedDecodeUnavailable',
        { reason, fallbackTo: 'wordListGrammar', scope: 'session' },
        { expected: true },
      );
      return;
    }

    const key = biasedGraphCacheKey(descriptor);
    if (this._warnedBiasedKeys.has(key)) {
      return;
    }
    this._warnedBiasedKeys.add(key);

    const isBuildFailure = reason === BIASED_FALLBACK_REASON.BuildFailed;
    if (!isBuildFailure) {
      // A store miss is retried every window, so this phrase may still upgrade.
      this._pendingBiasedPickupKeys.add(key);
    }

    // A build failure is an anomaly and warns; a store miss is the expected state for an
    // unpublished story and may still upgrade mid-phrase, so it is recorded as a
    // known-cause degradation instead. Passing the severity here keeps that distinction
    // through to the console, which a plain failure call would flatten to a warn.
    if (warm) {
      // The speculative label is provisional: remembered so the first LIVE
      // encounter with this key can emit the corrective, unlabeled record.
      this._warmOnlyWarnedKeys.add(key);
    }
    recordLocalAsrFailure(
      'graphs.phraseFallback',
      {
        reason,
        fallbackTo: 'wordListGrammar',
        storyId: descriptor?.storyId,
        phraseIndex: descriptor?.phraseIndex,
        error: isBuildFailure && err !== undefined ? (err?.message || String(err)) : undefined,
        // Only present when true, so pre-existing consumers of the event shape
        // see no change for live-decode fallbacks.
        warm: warm || undefined,
        // Key scope (BIASED_GRAPH_SCOPE_INTERVENTION): the intervention's scoped
        // key latches and reports independently of the phrase's, with the SAME
        // storyId/phraseIndex — this is what tells the two events apart. Absent
        // (not null) for phrase keys, so their shape is unchanged.
        graphScope: descriptor?.scope || undefined,
      },
      { expected: !isBuildFailure },
    );
  }

  /**
   * Classifies a failure on a biased recognizer that has NEVER replied as a CREATE
   * failure, whichever way it surfaced: a recognizer-scoped `error` event (create threw in
   * the worker) or a decode timeout (create WEDGED — no error event fires, so the first
   * window's event wait is the only place it becomes visible). Latches the key so later
   * windows use the word-list grammar instead of rebuilding the same doomed recognizer,
   * reports the degradation once, marks the error `biasedQualityFallback` so the handler
   * does not count it toward teardown, and feeds the systemic latch.
   *
   * No-op for grammar recognizers and for a biased one that has replied before: those are
   * runtime-health signals and keep ordinary evict + counted-failure semantics, so a truly
   * wedged WORKER still reaches teardown and the session falls back to server Kaldi.
   *
   * Also a no-op once the wrap is evicted, so only the path that OWNS the failure
   * classifies it. Both intentional callers classify before evicting; what this excludes is
   * an error arriving for a wrap another path already dropped (LRU eviction, or dispose).
   * A warm-built wrap has `everReplied === false` until its first decode, so without this a
   * late error on an LRU-passed-over wrap would latch a phrase whose graph built fine, and
   * one after dispose would record telemetry for a torn-down session. A create that really
   * failed still latches — the evicted key is out of the cache, so the next window rebuilds
   * and classifies on a live wrap.
   * @param {object} wrap
   * @param {Error} err
   * @returns {void}
   * @private
   */
  _handleBiasedWrapFailure(wrap, err) {
    if (!wrap.biased || wrap.everReplied || wrap.evicted) {
      return;
    }
    err.biasedQualityFallback = true;
    this._failedBiasedKeys.add(wrap.key);
    this._reportBiasedFallback(
      BIASED_FALLBACK_REASON.BuildFailed,
      wrap.descriptor,
      err,
      wrap.builtBy === 'speculativeWarm',
    );
    this._maybeLatchSystemicBiasedFailure();
  }

  /**
   * Disables biased decode for the session once build failures look systemic:
   * SYSTEMIC_BIASED_FAILURE_KEYS distinct keys failed and no biased decode has EVER
   * produced a worker reply. `_biasedGraphRuntime` is a contract assumption (the
   * manifest names a graph layer ⇒ the runtime has the binding), not a capability
   * probe — so a mislabeled runtime (the vosk-v3 dir serving a build without the
   * binding: an in-place rollback, stale CDN/cache bytes) passes the gate and then
   * fails EVERY phrase's create. Without this latch that scenario emits one
   * graphs.phraseFallback per phrase for the whole story and no session-level
   * signal; with it, the third failed key converts the noise into the same
   * once-per-session graphs.biasedDecodeUnavailable a correctly-labeled incapable
   * runtime gets, and the remaining phrases skip the doomed creates entirely.
   * Recorded as an anomaly (not expected): unlike the runtimeUnsupported case this
   * state means the published assets and the manifest disagree.
   * @returns {void}
   * @private
   */
  _maybeLatchSystemicBiasedFailure() {
    if (this._graphLoadUnsupported || this._biasedEverDecoded) {
      return;
    }
    // Distinct GRAPHS, not distinct cache keys. _failedBiasedKeys holds scoped keys, so a
    // phrase that also runs an intervention contributes two for one bad FST — which would
    // trip this latch after two bad graphs instead of three, and "phrase has an
    // intervention" is the ordinary case. The zero-successes gate above does NOT cover
    // that: the session it protects (the first couple of phrases have corrupt FSTs) is
    // precisely a session where nothing has decoded biased yet. Counting bases restores
    // the arithmetic the constant was calibrated against, and leaves the scoped key doing
    // its own job — the per-key retry latch, so a scope-specific failure cannot disable
    // the phrase's working recognizer.
    const distinctGraphs = new Set();
    for (const key of this._failedBiasedKeys) {
      distinctGraphs.add(unscopedBiasedGraphKey(key));
    }
    if (distinctGraphs.size < SYSTEMIC_BIASED_FAILURE_KEYS) {
      return;
    }
    this._graphLoadUnsupported = true;
    this._loggedGraphLoadUnsupported = true;
    recordLocalAsrFailure('graphs.biasedDecodeUnavailable', {
      reason: 'repeatedBuildFailures',
      fallbackTo: 'wordListGrammar',
      scope: 'session',
      // Both, because they differ once interventions are in play: failedKeys is what was
      // attempted, failedGraphs is what the threshold is measured against.
      failedKeys: this._failedBiasedKeys.size,
      failedGraphs: distinctGraphs.size,
    });
  }

  /**
   * Corrects an earlier `graphMissing` report for a phrase that went on to decode biased.
   *
   * On a slow network the opening window can miss the store while the download is still
   * in flight, then a later window picks the graph up. The per-key latch means the
   * graphMissing report would otherwise stand alone and read as if the whole phrase had
   * degraded — sending someone chasing a "degraded phrase" that was mostly fine. Emitted
   * once per key, and only for keys actually reported missing, so a phrase that had its
   * graph from the first window stays quiet.
   * @param {object} descriptor `{ storyId, phraseIndex }`
   * @returns {void}
   */
  _reportBiasedPickup(descriptor) {
    if (this._pendingBiasedPickupKeys.size === 0) {
      return;
    }
    const key = biasedGraphCacheKey(descriptor);
    if (!this._pendingBiasedPickupKeys.delete(key)) {
      return;
    }
    recordLocalAsrLifecycle('graphs.biasedPickedUpMidPhrase', {
      storyId: descriptor?.storyId,
      phraseIndex: descriptor?.phraseIndex,
      // Same discriminator _reportBiasedFallback carries: a phrase and its
      // intervention pick up the same graph under the SAME storyId/phraseIndex, so
      // without the scope their two events are indistinguishable. Absent (not null)
      // for phrase keys, so their shape is unchanged.
      graphScope: descriptor?.scope || undefined,
    });
  }

  /**
   * Returns a cached graph-backed recognizer for a per-story phrase, builds one from
   * the in-memory story-graph store, or returns null when the phrase's bytes are not
   * (yet) in the store — the caller then decodes via the word-list grammar. Store
   * misses are retried every window (cheap), so a phrase whose download completes
   * mid-phrase upgrades to the biased graph on the next window. Throws
   * BiasedGraphUnsupportedError when the runtime lacks the vosk-v3 per-recognizer
   * graph binding (stock 0.0.8 and the v2.x runtimes — see _biasedGraphRuntime).
   *
   * Graph-ownership contract (vosk-v3 runtime): `new KaldiRecognizer(sr, { graph })`
   * binds the graph to THAT recognizer, which owns it for life (freed on remove) —
   * ownership is per-recognizer, never shared-mutable. So caching one biased
   * recognizer per phrase key in the LRU is correct: each holds its own graph.
   * @param {object} descriptor `{ storyId, phraseIndex }`
   * @returns {Promise<object|null>}
   */
  async _getBiasedGraphRecognizer(descriptor) {
    const key = biasedGraphCacheKey(descriptor);
    const cached = this._touchRecognizer(key);
    if (cached) {
      return cached;
    }
    if (this._isBiasedKeyFailed(key)) {
      // The phrase is live now: if its only record is a speculative warm
      // failure, correct it before quietly falling back.
      this._correctSpeculativeWarmReport(key, descriptor);
      return null;
    }
    if (!this._biasedGraphRuntime) {
      throw new BiasedGraphUnsupportedError();
    }
    return this._buildBiasedGraphRecognizer(key, descriptor);
  }

  /**
   * Emits the corrective fallback report for a phrase whose build failure was so
   * far recorded only as a SPECULATIVE warm (`warm: true`) — the student is now
   * actually reading it, so the degradation is real. `deferredFromWarm` ties the
   * correction back to the earlier speculative event. Once per key; a phrase never
   * reached keeps its speculative-only record, which is the point of the label.
   *
   * TWO ESCAPES leave a read phrase labeled speculative, both accepted:
   *
   * 1. The only caller is gated behind `!_graphLoadUnsupported`, so once the systemic latch
   *    trips, keys already warned stay `warm: true`. Tolerable: the latch emits a
   *    session-scope graphs.biasedDecodeUnavailable, and post-latch phrases are silent by
   *    design — a coverage read has the stronger signal.
   * 2. `builtBy` is fixed at build and not demoted when the live path takes the wrap from
   *    cache, so an async create failure during the read is still labeled speculative.
   *    Usually self-correcting on the next window, but a failure surfacing on the phrase's
   *    LAST window has none. Rare: erroring creates report FIFO right after the create and
   *    wedged ones surface at the first window's timeout, so this needs a one-window phrase.
   * @param {string} key
   * @param {object} descriptor `{ storyId, phraseIndex }`
   * @returns {void}
   * @private
   */
  _correctSpeculativeWarmReport(key, descriptor) {
    if (!this._warmOnlyWarnedKeys.delete(key)) {
      return;
    }
    recordLocalAsrFailure(
      'graphs.phraseFallback',
      {
        reason: BIASED_FALLBACK_REASON.BuildFailed,
        fallbackTo: 'wordListGrammar',
        storyId: descriptor?.storyId,
        phraseIndex: descriptor?.phraseIndex,
        // Same discriminator the other two phraseFallback emitters carry. Unreachable
        // with a scoped key today (interventions are never warmed), but an intervention
        // pre-warm is the documented fix if create stalls appear in the field, and that
        // is the change that would start minting deferredFromWarm records a phrase and
        // its intervention share.
        graphScope: descriptor?.scope || undefined,
        deferredFromWarm: true,
      },
      { expected: false },
    );
  }

  /**
   * Whether this key, or the GRAPH it is a scope of, is already known to fail.
   *
   * Stored scoped, read scoped-or-base, deliberately asymmetric. A phrase and its
   * intervention resolve the SAME bytes, so once the phrase's build fails the
   * intervention's is doomed too — and letting it try is not one wasted create but, for a
   * create that WEDGES the worker, another DEFAULT_DECODE_TIMEOUT_MS (8s) burned inline on
   * the intervention's first stride. That outlives Listen's own 5s maxListenTime, so the
   * intervention returns nothing at all and the student hears the silence patter.
   *
   * The reverse does not hold: an intervention-only failure never blocks the phrase, which
   * is the property the scoped key exists for.
   * @param {string} key
   * @returns {boolean}
   * @private
   */
  _isBiasedKeyFailed(key) {
    return this._failedBiasedKeys.has(key)
      || this._failedBiasedKeys.has(unscopedBiasedGraphKey(key));
  }

  /**
   * Reads the phrase's HCLG bytes from the story-graph store, evicts to make room,
   * builds a graph-backed recognizer (one-step vosk-v3 create over the shared
   * acoustic model), and caches it under `key`. The caller has already checked the
   * cache and runtime support. Null when the bytes are not in the store. A build
   * failure marks the key failed so it is not re-attempted every window.
   *
   * `fstBytes` is a Uint8Array VIEW over the store's buffer (getStoryGraphBytes'
   * contract): the runtime copies views before transferring to the worker, so the
   * stored bytes survive this create and any later re-read.
   * @param {string} key
   * @param {object} descriptor
   * @returns {Promise<object|null>}
   */
  async _buildBiasedGraphRecognizer(key, descriptor, builtBy = 'decode') {
    const fstBytes = getStoryGraphBytes(descriptor.storyId, descriptor.phraseIndex);
    if (!fstBytes) {
      return null;
    }
    this._ensureCacheCapacity();
    try {
      // NOT a worker round-trip: the v3 create posts the graph to the worker and
      // returns without an ack, so this settles on the next microtask and needs no
      // staging timeout (the v2 loadGraph path awaited the per-phrase AM re-parse
      // and did). Synchronous throws here are client-side (e.g. model not ready);
      // WORKER-side create failures surface later — as a recognizer-scoped `error`
      // event when the create throws, or as the first window's decode timeout when
      // it wedges — and both routes classify through _handleBiasedWrapFailure,
      // which latches this key failed and the recognizer is evicted.
      const rec = await createBiasedGraphRecognizer(
        this._model,
        fstBytes,
        { sampleRate: this.sampleRate },
      );
      const wrap = this._wireRecognizer(key, rec, true, descriptor);
      // 'decode' | 'warm' (startPhrase — the phrase IS being read) |
      // 'speculativeWarm' (endPhrase, phraseIndex + 1 — may never be read). An
      // async create failure reports through _handleBiasedWrapFailure after the
      // fact, and only the speculative origin may be labeled `warm: true` — a
      // mislabeled current-phrase failure would hide a real degradation from
      // per-phrase biased-coverage reads.
      wrap.builtBy = builtBy;
      return wrap;
    } catch (err) {
      this._failedBiasedKeys.add(key);
      this._maybeLatchSystemicBiasedFailure();
      throw err;
    }
  }

  /**
   * Best-effort prefetch of a phrase's biased-graph recognizer so its first decode
   * window is a cache hit instead of paying the create inline. On v3 the create is
   * cheap (~tens of ms — the FST is read against the already-parsed acoustic
   * model), so unlike the v2 warm this needs no scheduling care: the consumer
   * warms the CURRENT phrase at startPhrase (the first stride's ~200 ms of runway
   * hides the create even on slow hardware — the exact call site that stalled for
   * seconds under v2's AM re-parse) and the NEXT phrase at endPhrase (the
   * inter-phrase break, extra runway for devices where the create exceeds one
   * stride). Insurance for the hardware the ~32 ms bench median does not
   * represent, not a correctness dependency: a missed or wrong-phrase warm just
   * means the live path builds inline, exactly as if this were never called.
   *
   * Runs on the SAME serialized decode queue as transcribe. Fully best-effort: a
   * runtime without the graph binding, bytes not in the story-graph store
   * (descriptor past the end of the story, download not done), or any build error
   * resolves to false without throwing — the live path re-attempts store misses
   * per window and falls back to the word-list grammar. A build that actually
   * happened records `graphs.prewarmed` (with its queue-inclusive duration) so a
   * warm sitting in front of live work stays visible in triage; a cache hit is
   * silent.
   * @param {object} descriptor `{ storyId, phraseIndex }`
   * @param {{speculative?: boolean}} [options] `speculative: true` for a warm of a
   *   phrase the student is NOT currently reading (the endPhrase `phraseIndex + 1`
   *   warm). Only speculative failures are labeled `warm: true` in telemetry — a
   *   startPhrase warm fronts the phrase being read, so its failure is a real
   *   degradation and reports unlabeled.
   * @returns {Promise<boolean>} true when a recognizer is cached for the descriptor.
   */
  warmBiasedGraph(descriptor, { speculative = false } = {}) {
    // `_graphLoadUnsupported` matters here as much as on the decode path: the warm
    // runs at EVERY phrase boundary, so without it a session whose biased decode
    // was latched off (unsupported runtime, systemic build failures) would keep
    // running one doomed create per remaining phrase — each with a fresh key that
    // the per-key latch cannot stop — undoing exactly the noise reduction and
    // skipped work the session latch exists for.
    if (!descriptor || this._disposing || !this._biasedGraphRuntime || this._graphLoadUnsupported) {
      return Promise.resolve(false);
    }
    // Timed from entry (including queue wait behind live decodes) because a warm
    // only helps when it finishes before the audio it fronts for.
    const startedAt = now();
    const run = async () => {
      // Re-checked on the queue: the latch may have fired while this warm waited
      // behind the decode (or warm) that tripped it.
      if (!this._model || this._disposing || this._graphLoadUnsupported) {
        return false;
      }
      const key = biasedGraphCacheKey(descriptor);
      if (this._touchRecognizer(key)) {
        return true;
      }
      if (this._isBiasedKeyFailed(key)) {
        return false;
      }
      try {
        const warmed = Boolean(await this._buildBiasedGraphRecognizer(
          key,
          descriptor,
          speculative ? 'speculativeWarm' : 'warm',
        ));
        if (warmed) {
          recordLocalAsrLifecycle('graphs.prewarmed', {
            storyId: descriptor?.storyId,
            phraseIndex: descriptor?.phraseIndex,
            durationMs: Math.round(now() - startedAt),
          });
        }
        return warmed;
      } catch (err) {
        // Same reporting path as a live-decode build failure (and the async error
        // listener), so the phrase's degradation is labeled buildFailed once —
        // the shared per-key latch keeps later store-miss windows quiet. Only a
        // SPECULATIVE warm (endPhrase, phraseIndex + 1 — a phrase the student may
        // never read) is labeled `warm: true`; a startPhrase warm failure is the
        // read phrase's real degradation.
        this._reportBiasedFallback(BIASED_FALLBACK_REASON.BuildFailed, descriptor, err, speculative);
        return false;
      }
    };
    const result = this._tail.then(run, run);
    this._tail = result.then(noop, noop);
    return result;
  }

  /** Releases all recognizers and the model. */
  async dispose() {
    // Block new decodes, then wait for any in-flight one to settle so teardown never
    // races the worker (a warmup decode can outlive the handler's drain timeout).
    // Bounded by the decode timeout via _tail.
    this._disposing = true;
    await this._tail.catch(() => {});
    // Through _evict for the `evicted` guard: a worker error event arriving after
    // teardown must not post a second remove for an id this loop already removed.
    // (Deleting the current entry during Map iteration is safe.)
    for (const wrap of this._recognizers.values()) {
      this._evict(wrap);
    }
    this._recognizers.clear();
    if (this._model && typeof this._model.terminate === 'function') {
      try {
        await this._model.terminate();
      } catch (_) {
        // terminate() throws only if the worker is already gone; the model is
        // being discarded here anyway, so nothing downstream depends on it. Teardown
        // continues and _model is nulled regardless.
      }
    }
    this._model = null;
  }

  /**
   * Returns a cached recognizer for the grammar (LRU: most-recent last), creating
   * one on miss and evicting the least-recently-used when full. `streamSlot`
   * scopes the cache key only (see grammarCacheKey) — the recognizer is built
   * from the same grammar either way.
   * @param {string|null} grammar
   * @param {string} [streamSlot]
   * @returns {object}
   */
  _getRecognizer(grammar, streamSlot) {
    const key = grammarCacheKey(grammar, streamSlot);
    const cached = this._touchRecognizer(key);
    if (cached) {
      return cached;
    }
    this._ensureCacheCapacity();
    const rec = grammar
      ? new this._model.KaldiRecognizer(this.sampleRate, grammar)
      : new this._model.KaldiRecognizer(this.sampleRate);
    return this._wireRecognizer(key, rec, false);
  }

  /**
   * Returns the cached recognizer for `key`, moving it to most-recently-used so it is
   * never the next eviction target. Null on miss.
   * @param {string} key
   * @returns {object|null}
   */
  _touchRecognizer(key) {
    const cached = this._recognizers.get(key);
    if (!cached) {
      return null;
    }
    // Re-insert so the active recognizer is never the eviction target.
    this._recognizers.delete(key);
    this._recognizers.set(key, cached);
    return cached;
  }

  /**
   * Frees least-recently-used recognizers so a new one fits, before it is constructed.
   * One shared bound for every recognizer kind: on the vosk-v3 runtime a biased
   * recognizer owns only its own ~50 KB graph (the base acoustic model is shared),
   * so the separate lower biased cap the v2 loadGraph path needed is gone — see
   * MAX_CACHED_RECOGNIZERS.
   * @returns {void}
   */
  _ensureCacheCapacity() {
    while (this._recognizers.size >= MAX_CACHED_RECOGNIZERS) {
      this._evictLru();
    }
  }

  /**
   * Evicts the least-recently-used recognizer (oldest in insertion order). No-op if
   * the cache is empty. Frees the evicted recognizer.
   *
   * A recognizer a live stream owns (the phrase's, or a mid-phrase intervention's —
   * see _adoptStreamingWrap's slots) is passed over while anything else is
   * evictable. Evicting one discards its accumulated transcript — the emitted
   * transcript collapses and the next stride re-pays the graph create — and unlike a
   * window-decode clobber it is invisible: eviction never reaches _decodeWindow's
   * canary. Only its own strides touch a streaming recognizer, so a phrase where the
   * student is stuck (no strides) while other recognizers are inserted around it
   * would otherwise walk it to the front of the LRU order.
   * @returns {void}
   */
  _evictLru() {
    if (this._evictLruMatching(true)) {
      return;
    }
    // Nothing else matched: the caller needs the slot, so the streaming recognizer
    // goes rather than leaving the cache over its cap.
    this._evictLruMatching(false);
  }

  /**
   * Evicts the oldest recognizer, optionally passing over one a streaming phrase owns.
   * Routed through _evict so the eviction is idempotent against a late worker error:
   * an LRU-evicted wrap whose create failure is still in flight would otherwise get
   * a second `remove` from the error listener's evict.
   * @param {boolean} skipStreaming
   * @returns {boolean} whether a recognizer was evicted.
   * @private
   */
  _evictLruMatching(skipStreaming) {
    for (const wrap of this._recognizers.values()) {
      if (skipStreaming && wrap.streaming) {
        continue;
      }
      this._evict(wrap);
      return true;
    }
    return false;
  }

  /**
   * Enables words, wires the result/partialresult/error events into a wrap, caches it
   * under `key`, and returns it.
   * @param {string} key
   * @param {object} rec vosk KaldiRecognizer
   * @param {boolean} biased whether `rec` is graph-backed (owns its per-phrase graph).
   * @param {{storyId: string, phraseIndex: number}} [descriptor] Biased-graph
   *   descriptor, kept on the wrap so the error listener can report which phrase
   *   degraded.
   * @returns {object}
   */
  _wireRecognizer(key, rec, biased, descriptor) {
    this._recognizerBuilds += 1;
    rec.setWords(true);
    // `streaming` marks a recognizer mid-phrase on the streaming path, so a window
    // decode about to reset it is detectable (see _warnStreamingClobber).
    // `everReplied` is monotonic (unlike `events`, which resets per window): it
    // separates a create-phase worker error (no reply ever) from a decode-time one
    // on a recognizer that provably works — see the error listener below.
    const wrap = {
      key, rec, biased: Boolean(biased), descriptor, results: [], events: 0, everReplied: false, waiter: null, partial: '', streaming: false, pendingUtterance: false,
    };
    const onEvent = () => {
      wrap.events += 1;
      wrap.everReplied = true;
      if (wrap.biased) {
        // A worker reply from a graph-carrying recognizer proves the runtime
        // genuinely has the binding; see _maybeLatchSystemicBiasedFailure.
        this._biasedEverDecoded = true;
      }
      if (wrap.waiter && wrap.events >= wrap.waiter.target) {
        const { resolve, timer } = wrap.waiter;
        wrap.waiter = null;
        clearTimeout(timer);
        resolve();
      }
    };
    rec.on('result', (message) => {
      const r = message && message.result;
      if (r && (r.text || r.result)) {
        wrap.results.push(r);
      }
      // An endpoint result commits the segment; the live partial restarts empty.
      wrap.partial = '';
      onEvent();
    });
    rec.on('partialresult', (message) => {
      wrap.partial = (message && message.result && message.result.partial) || '';
      onEvent();
    });
    // Worker-side failures for THIS recognizer arrive as `error` events — on v3
    // there is no staging await to reject, so a failed create (e.g. corrupt biased
    // FST bytes failing C++ construction) reports here, after the recognizer was
    // cached. Without this listener the error is silently dropped and every later
    // window on this recognizer stalls to the decode timeout, each counting toward
    // the handler's consecutive-failure teardown — one bad published FST could end
    // local ASR for the session.
    //
    // Only a CREATE-phase failure (`!wrap.everReplied` — the worker never answered
    // audio) latches the biased key, reports buildFailed, and is marked so the
    // handler does not count it toward the consecutive-failure teardown
    // (_handleBiasedWrapFailure; the same classification also covers a create
    // that WEDGES instead of erroring, via the decode-timeout catches). An error
    // on a recognizer that has replied before is a decode-time fault (e.g. a
    // transient buffer-allocation failure): latching it would permanently
    // downgrade a phrase whose graph provably works, so that case just evicts and
    // the next window rebuilds from the store, keeping ordinary counted-failure
    // semantics. Either way the dead recognizer is evicted and any in-flight wait
    // fails immediately rather than sitting out the decode timeout.
    //
    // Deliberately wired for EVERY recognizer, not just biased ones: a grammar or
    // free-decode recognizer's worker error takes the same evict + immediate
    // rejection path with ordinary counted-failure semantics — the same failure
    // count the decode timeout produced before, just ~8 s sooner.
    rec.on('error', (message) => {
      const err = new Error((message && message.error) || 'VoskAsr: recognizer error');
      this._handleBiasedWrapFailure(wrap, err);
      this._evict(wrap);
      if (wrap.waiter) {
        const { reject, timer } = wrap.waiter;
        wrap.waiter = null;
        clearTimeout(timer);
        reject(err);
      }
    });
    this._recognizers.set(key, wrap);
    return wrap;
  }

  /**
   * Feeds one window, flushes the final result, and returns text + confidences.
   *
   * The vosk-browser worker replies with exactly one message per call:
   * acceptWaveform -> `result` (on an endpoint) or `partialresult` (otherwise), and
   * retrieveFinalResult -> `result`. Both are posted FIFO, so we pipeline them and
   * wait for the two events, then assemble. `partialresult` isn't collected, and any
   * endpoint `result` covers a disjoint segment from the final one, so concatenation
   * reconstructs the window transcript without doubling. Awaiting the events (not
   * polling) keeps the per-stride path latency-free; on a timeout the recognizer is
   * dropped so its late events can't corrupt the next decode and the error propagates
   * so the handler counts a failure.
   * @param {object} wrap
   * @param {Float32Array} pcm
   * @returns {Promise<{text: string, wordScores: Float32Array|undefined}>}
   */
  async _decodeWindow(wrap, pcm) {
    if (wrap.streaming) {
      this._warnStreamingClobber(wrap.key);
      this._releaseStreamingWrap(wrap);
      wrap.partial = '';
    }
    wrap.results.length = 0;
    wrap.events = 0;
    wrap.waiter = null;
    try {
      wrap.rec.acceptWaveform(this._toAudioBuffer(pcm));
      wrap.pendingUtterance = true;
      wrap.rec.retrieveFinalResult();
      await waitForEvents(wrap, 2, this._decodeTimeoutMs);
      wrap.pendingUtterance = false;
    } catch (err) {
      // A TIMEOUT on a biased recognizer that never replied is a create failure
      // that never surfaced (a wedged create posts no error event) — latch it so
      // the next window falls back to the grammar instead of rebuilding the same
      // doomed recognizer every stride. See _handleBiasedWrapFailure.
      //
      // Gated on `decodeTimeout` because this try also covers the SYNCHRONOUS
      // acceptWaveform/retrieveFinalResult calls above it. A client-side throw
      // there is a bug in this code, not a worker create failure: classifying it
      // would permanently latch a phrase whose graph was never tested, exempt the
      // failure from the teardown counter, and — at three such faults — log
      // biasedDecodeUnavailable{repeatedBuildFailures}, pointing triage at the CDN.
      // A worker `error` event needs no branch here: the listener classifies before
      // it rejects this waiter, so skipping it also drops a redundant second call.
      if (err.decodeTimeout) {
        this._handleBiasedWrapFailure(wrap, err);
      }
      this._evict(wrap);
      throw err;
    }
    return collectResult(wrap.results);
  }

  /**
   * Warns once per session when a window decode lands on a recognizer that a
   * streaming phrase owns. _decodeWindow clears wrap.results and resets the decoder's
   * utterance state, so this discards the phrase's accumulated streaming transcript —
   * the emitted transcript collapses to whatever is decoded after it, stalling the
   * cursor mid-phrase.
   *
   * The handler keeps concurrent decode paths on separate recognizers (interventions
   * stream against a phrase-SCOPED cache key over the same graph bytes, see
   * LocalPlacementAsrHandler._captureBiasedGraph), so reaching here means a caller
   * regressed that separation. Diagnostic only: the decode still proceeds, because
   * failing it would trip the handler's consecutive-failure teardown and abandon local
   * ASR over what is a quality regression, not an inference failure.
   * @param {string} key
   * @returns {void}
   * @private
   */
  _warnStreamingClobber(key) {
    if (this._warnedStreamingClobber || typeof console === 'undefined' || typeof console.warn !== 'function') {
      return;
    }
    this._warnedStreamingClobber = true;
    console.warn(`VoskAsr: window decode on streaming recognizer ${key}; its phrase transcript is discarded`);
  }

  /**
   * Drops a recognizer from the cache and releases it. Idempotent: a worker-side
   * create failure reaches here TWICE — the error listener evicts, then rejects
   * the in-flight event wait, whose catch evicts again — and without the guard
   * each pass posted its own `remove` for the same recognizer id. The worker
   * answers the second with a recognizer-scoped "does not exist" error; today
   * that lands harmlessly (the vendor recognizer unregisters itself on the first
   * remove, so the reply falls through to the Model, where createModel's settled
   * guard drops it), but that safety is a vendor implementation detail, not a
   * contract — if the reply ever routed back to the recognizer it would re-enter
   * the error listener and evict again.
   * @param {object} wrap
   */
  _evict(wrap) {
    if (wrap.evicted) {
      return;
    }
    wrap.evicted = true;
    if (this._recognizers.get(wrap.key) === wrap) {
      this._recognizers.delete(wrap.key);
    }
    this._releaseStreamingWrap(wrap);
    removeRecognizer(wrap);
  }

  /**
   * @param {Float32Array} pcm
   * @returns {AudioBuffer|Float32Array}
   */
  _toAudioBuffer(pcm) {
    if (!this._bufCtx || typeof this._bufCtx.createBuffer !== 'function') {
      return pcm;
    }
    const buffer = this._bufCtx.createBuffer(1, Math.max(1, pcm.length), this.sampleRate);
    buffer.copyToChannel(pcm, 0);
    return buffer;
  }
}

/**
 * @param {number} sampleRate
 * @returns {OfflineAudioContext|null}
 */
function makeBufferContext(sampleRate) {
  const Ctx = (typeof globalThis !== 'undefined')
    && (globalThis.OfflineAudioContext || globalThis.webkitOfflineAudioContext);
  return Ctx ? new Ctx(1, 1, sampleRate) : null;
}

/**
 * Flattens result events into transcript text and per-word confidences (clamped
 * to [0,1]). Confidence-bearing word arrays are preferred; plain text is scored 0.5.
 * @param {object[]} results
 * @returns {{text: string, wordScores: Float32Array|undefined}}
 */
function collectResult(results) {
  const words = [];
  const scores = [];
  for (const r of results) {
    if (Array.isArray(r.result) && r.result.length > 0) {
      for (const w of r.result) {
        if (w && w.word) {
          const conf = clamp01(typeof w.conf === 'number' ? w.conf : 0.5);
          for (const part of splitWords(w.word)) {
            words.push(part);
            scores.push(conf);
          }
        }
      }
    } else if (r.text) {
      for (const w of splitWords(r.text)) {
        words.push(w);
        scores.push(0.5);
      }
    }
  }
  return words.length > 0
    ? { text: words.join(' '), wordScores: Float32Array.from(scores) }
    : { text: '', wordScores: undefined };
}

// Vosk grammar/biased recognizers emit "[unk]" for out-of-grammar audio. The
// server-Kaldi path emits "<UNK>", and ReaderManager's UNK-count intervention
// suppression matches that form (case-sensitive) — normalize so the local
// streaming path feeds the same signal. scoreTranscript ignores unks either way.
function normalizeUnk(word) {
  return word.toLowerCase() === '[unk]' ? '<UNK>' : word;
}

/**
 * Cumulative streaming transcript: committed endpoint segments (wrap.results,
 * which streamChunk never clears mid-phrase) followed by the live partial.
 * Partial words carry the 0.5 placeholder confidence collectResult uses for
 * confidence-less text.
 * @param {object} wrap
 * @returns {{text: string, words: string[], wordScores: Float32Array|undefined}}
 */
function streamResult(wrap) {
  const committed = collectResult(wrap.results);
  const words = [...splitWords(committed.text), ...splitWords(wrap.partial)].map(normalizeUnk);
  if (words.length === 0) {
    return { text: '', words, wordScores: undefined };
  }
  const scores = committed.wordScores ? Array.from(committed.wordScores) : [];
  while (scores.length < words.length) {
    scores.push(0.5);
  }
  return { text: words.join(' '), words, wordScores: Float32Array.from(scores) };
}

/**
 * Releases a recognizer. remove() throws only if the worker is already gone; the
 * recognizer is being discarded, so the error is not actionable and teardown of
 * the remaining recognizers continues.
 * @param {object} wrap
 */
function removeRecognizer(wrap) {
  if (wrap && wrap.rec && typeof wrap.rec.remove === 'function') {
    try {
      wrap.rec.remove();
    } catch (_) {
      // See doc comment: nothing downstream depends on this recognizer.
    }
  }
}

/**
 * Resolves once `wrap.events` reaches `target`, or rejects after `timeoutMs` so a
 * wedged worker surfaces as an inference failure. Only one waiter is live at a time
 * (transcribe is serialized).
 *
 * The timeout rejection carries `decodeTimeout` because the decode catches wrap the
 * synchronous acceptWaveform/retrieveFinalResult calls as well as this await, and
 * only the await's expiry means "the worker never answered". See the flag's use at
 * those catch sites.
 * @param {object} wrap
 * @param {number} target
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
function waitForEvents(wrap, target, timeoutMs) {
  if (wrap.events >= target) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      wrap.waiter = null;
      const timedOut = new Error(`VoskAsr: decode timed out after ${timeoutMs}ms`);
      timedOut.decodeTimeout = true;
      reject(timedOut);
    }, timeoutMs);
    // `reject` is stored so a recognizer-scoped worker error (see _wireRecognizer's
    // error listener) can fail the in-flight decode immediately instead of letting
    // it sit out the full decode timeout on events that will never arrive.
    wrap.waiter = { target, resolve, reject, timer };
  });
}

/**
 * @param {Float32Array|ArrayLike<number>} input
 * @returns {Float32Array}
 */
function toFloat32(input) {
  return input instanceof Float32Array ? input : Float32Array.from(input || []);
}

/**
 * @param {string} text
 * @returns {string[]}
 */
function splitWords(text) {
  return text ? text.split(/\s+/).filter((w) => w.length > 0) : [];
}

/**
 * @param {number} value
 * @returns {number}
 */
function clamp01(value) {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

// Exported for unit tests.
export { collectResult as _collectResult };
